import AddGroupBooking from '@/components/client/events/EventPricing/AddGroupBooking'
import {
  AddRemindMeDocument,
  BuyCertificateDocument,
  EventDetailsOutputQuery,
  FindInvoiceDocument,
  GetCoursePriceDocument,
  HasBookingDocument,
  RemoveUserFromGroupDocument,
} from '@/types/graphql/types'
import { getErrorMessage } from '@/util/getErrorMessage'
import { sendTelegramMessage } from '@/util/telegram'
import { useMutation, useQuery } from '@apollo/client'
import { useForm } from '@mantine/form'
import { useModals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { BOOKING_STATUS, METHOD_OF_DELIVERY, PRICING_PLAN_TYPE } from 'db'
import { toString } from 'lodash'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

require('dayjs/locale/ar')

dayjs.extend(relativeTime)

const toHashtag = (str: string) =>
  (str || '')
    .toLocaleLowerCase()
    .replace(/\s/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')

type GroupFormType = {
  isGroupPay: boolean
  groupTrainee: Array<{ email: string }>
  methodOfDelivery: 'ONLINE' | 'ONSITE' | undefined
}

const useEventPricing = ({
  event,
}: {
  event: EventDetailsOutputQuery['EventDetailsOutput']
}) => {
  const modals = useModals()
  const [hideAlreadyBookedMessage, setHideAlreadyBookedMessage] =
    useState<boolean>(false)
  const { t, lang } = useTranslation('client')
  const router = useRouter()
  const isArabic = lang === 'ar'
  const { data } = useSession()
  const isLoggedIn = !!data?.user
  const locale = router.locale === 'en' ? '/en' : '/ar'
  const containsWorkshop =
    event?.eventParts?.some(e => e?.pricingPlan.hasWorkshop === true) ||
    event?.pricingPlan?.hasWorkshop === true
  const isCoursePaid = [
    PRICING_PLAN_TYPE.paid.id,
    PRICING_PLAN_TYPE.basedOnSpecialty.id,
  ].includes(event.pricingPlan!.pricingPlanType?.id ?? 0)

  const form = useForm({
    initialValues: {
      parts: event.eventParts ? event.eventParts?.map(e => e?.id!) : [],
      workshop: containsWorkshop,
      isGroupRegistrant: false,
      groupTraineeEmails: [] as string[],
      isGroupPay: undefined as boolean | undefined,
      methodOfDelivery: (event.methodOfDelivery!.id ===
      METHOD_OF_DELIVERY.onsite.id
        ? 'ONSITE'
        : 'ONLINE') as 'ONLINE' | 'ONSITE' | undefined,
      isCertificateRegistrant:
        event.pricingPlan!.pricingPlanType?.id == PRICING_PLAN_TYPE.free.id
          ? false
          : true,
    },
  })
  //calculate price for event
  const {
    data: priceData,
    loading,
    error,
  } = useQuery(GetCoursePriceDocument, {
    skip: !isLoggedIn,
    variables: {
      data: {
        eventId: event.id!,
        partsIds: form.values.parts,
        isWorkshopRegistrant: form.values.workshop,
        isGroupRegistrant: form.values.isGroupRegistrant,
        groupTraineeEmails: form.values.groupTraineeEmails,
        isGroupPay: form.values.isGroupPay,
        methodOfDelivery: form.values.methodOfDelivery as any,
        isCertificateRegistrant: form.values.isCertificateRegistrant,
      },
    },
  })

  //open group booking form
  const openGroupModal = () =>
    modals.openModal({
      title: t('groupDiscount'),
      children: (
        <div>
          <AddGroupBooking
            onSubmit={values => {
              form.setValues({
                ...form.values,
                isGroupRegistrant: true,
                groupTraineeEmails: values.groupTrainee.map(e => e.email),
                isGroupPay: values.isGroupPay,
              })
              modals.closeAll()
            }}
          />
        </div>
      ),
    })

  const price =
    priceData?.getCoursePrice.traineeEvent.find(
      te => te?.trainee?.email == localStorage.getItem(`userEmail`),
    ) || priceData?.getCoursePrice.traineeEvent[0]
  const groupPrice = priceData?.getCoursePrice.traineeEventGroup
  const isGroupPay = groupPrice?.isGroupPay
  const showGroupData = !!priceData?.getCoursePrice.traineeEventGroup
  const tax = showGroupData ? groupPrice?.groupTax : price?.tax ?? 0
  //only show price per every user  (23/8/2023)
  const totalToPay =
    showGroupData && isGroupPay
      ? groupPrice?.groupTotalToPay
      : price?.totalToPay ?? event.price ?? 0
  const totalToPayWithoutTax = +(totalToPay - tax).toFixed(2)
  const usedWalletBallance =
    (showGroupData ? groupPrice?.groupWalletBalance : price?.walletBalance) ?? 0
  const workshopPrice =
    (showGroupData ? groupPrice?.groupWorkshopPrice : price?.workshopPrice) ?? 0
  const pricingPlan = priceData?.getCoursePrice.event.pricingPlan

  const isJustRegister =
    pricingPlan?.pricingPlanTypeId == PRICING_PLAN_TYPE.free.id &&
    pricingPlan?.freePlanCertificateStatus == 'justRegister'

  const isCertificateAndCMEIsFree =
    pricingPlan?.pricingPlanTypeId == PRICING_PLAN_TYPE.free.id &&
    pricingPlan?.freePlanCertificateStatus == 'certificateAndCMEIsFree'

  const isCertificateAndCMEText =
    event.isAccredited ||
    (pricingPlan?.pricingPlanTypeId == PRICING_PLAN_TYPE.free.id &&
      ['certificateAndCMEIsPaid', 'certificateAndCMEIsFree'].includes(
        pricingPlan?.freePlanCertificateStatus ?? '',
      ))

  const isCertificateFreeText =
    event.isAccredited ||
    (pricingPlan?.pricingPlanTypeId == PRICING_PLAN_TYPE.free.id &&
      ['certificateIsFree'].includes(
        pricingPlan?.freePlanCertificateStatus ?? '',
      ))

  const isFree =
    (pricingPlan?.pricingPlanTypeId == PRICING_PLAN_TYPE.free.id &&
      ['justRegister', 'certificateAndCMEIsFree', 'certificateIsFree'].includes(
        pricingPlan?.freePlanCertificateStatus ?? '',
      )) ||
    totalToPayWithoutTax === 0

  let errorMessage =
    t((error?.networkError as any)?.result?.errors[0]?.message).split(',')[0] ??
    ''

  if (errorMessage === 'Not Authorised!' && isLoggedIn === false)
    errorMessage = t('getPriceNotAuthedError')

  const certificatePrice =
    showGroupData && isGroupPay
      ? groupPrice?.groupCertificatePrice ?? 0
      : price?.certificatePrice ?? 0

  const errorAlreadyRegistredMemebers = errorMessage
    ?.split('GROUP_MEMBER_ALREADY_REGISTERED')[1]
    ?.split(',')
  //fetch booking data
  const { data: hasBookingData } = useQuery(HasBookingDocument, {
    variables: {
      where: {
        traineeId_eventId: {
          traineeId: data?.user.id!,
          eventId: event.id!,
        },
      },
    },
    skip: !data?.user.id,
  })

  const bookingStatusId =
    hasBookingData?.findUniqueTraineeEvent?.bookingStatus?.id

  const noSeatsAvailableError = priceData?.getCoursePrice.seatsError

  const [ref, setRef] = useState<string | undefined>(undefined)
  const [refTemp, setTempRef] = useState<string | undefined>(undefined)
  const { data: userData } = useSession()
  useEffect(() => {
    getRef()
  }, [userData?.user?.id, router.query.id, router.query.ref])
  useEffect(() => {
    getRef()
  }, [])
  //get referral id
  const getRef = async () => {
    const tempRef = localStorage.getItem(`refcode`)
    const _ref = localStorage.getItem(`ref:${event.id}`)
    if (_ref) {
      setRef(_ref)
      return
    }
    if (tempRef) {
      setTempRef(tempRef)
      return
    }
    //set referral id in local storage
    if (
      router.query.ref &&
      router.query.ref !== toString(userData?.user?.id ?? '')
    ) {
      setRef(router.query.ref as string)
      try {
        localStorage.setItem(
          `ref:${router.query.id}`,
          router.query.ref as string,
        )
      } catch (e) {
        await sendTelegramMessage(
          `failed to set ref code ${router.query.ref}  id ${router.query.id} ----- ${e}`,
        )
      }
    }
  }
  const expiredGroupDiscount =
    event.pricingPlan?.hasGroupDiscountEndDate &&
    dayjs(event.pricingPlan?.groupDiscountEndDate).isAfter(new Date())
  const shareLink = typeof window === 'undefined' ? '#' : window.location.href

  const groupDiscountMessage = `
 للإستفادة من خصم المجموعات لدورة  ${event.titleAr} تواصل معي لنكون مجموعة.
  `

  const hastag = `infinity_${toHashtag(
    event.titleEn || toString(event.id) || '',
  )}`
  const groupDiscountMessageWithHastag = `
  للإستفادة من خصم المجموعات إستخدم الهاشتاق #${hastag} لتكوين مجموعة, وكن انت اول من يجمع الفريق.
  ${event.titleAr} 
    `

  const [remindMe, { loading: remindmeLoading }] = useMutation(
    AddRemindMeDocument,
    {
      onCompleted: () => {
        showNotification({
          title: t('notifyMe'),
          message: t('youWillBeNotified'),
        })
      },
      onError(e) {
        showNotification({
          title: t('error'),
          message: e.message,
          color: 'red',
          autoClose: false,
        })
      },
    },
  )

  //----------Buy Certificate-------------//
  const { data: InvoiceData, loading: loadingInvoice } = useQuery(
    FindInvoiceDocument,
    {
      skip: !isLoggedIn,
      variables: {
        where: {
          traineeId_eventId: {
            traineeId: data?.user.id!,
            eventId: event.id!,
          },
        },
      },
    },
  )

  const shouldShowBuyCertificate =
    pricingPlan?.pricingPlanTypeId == PRICING_PLAN_TYPE.free.id &&
    pricingPlan?.freePlanCertificateStatus == 'certificateAndCMEIsPaid' &&
    InvoiceData?.findUniqueTraineeEvent?.certificatePrice == 0 &&
    InvoiceData?.findUniqueTraineeEvent.totalToPay == 0 &&
    bookingStatusId == BOOKING_STATUS.paid.id

  //update booking mutation
  const [mutate, { loading: loadingBuyCertificate }] = useMutation(
    BuyCertificateDocument,
    {
      onCompleted() {
        router.push(
          `/payment/${event.id}?bookingId=${hasBookingData?.findUniqueTraineeEvent?.id}`,
        )

        showNotification({
          message: t('updatedSuccessfully'),
        })
      },
      onError(error) {
        showNotification({
          title: t('error'),
          message: getErrorMessage(error),
          color: 'red',
          autoClose: false,
        })
      },
    },
  )

  const buyCertificate = () => {
    form.setFieldValue('isCertificateRegistrant', true)
    mutate({
      variables: {
        where: {
          traineeId_eventId: {
            traineeId: data?.user.id!,
            eventId: event.id!,
          },
        },
        data: {},
      },
    })
  }
  return {
    shouldShowBuyCertificate,
    loadingBuyCertificate,
    buyCertificate,
    form,
    price,
    groupPrice,
    isGroupPay,
    showGroupData,
    loading: loading || loadingBuyCertificate || loadingInvoice,
    isLoggedIn,
    error,
    tax,
    hasBookingData,
    setHideAlreadyBookedMessage,
    totalToPay,
    totalToPayWithoutTax,
    usedWalletBallance,
    workshopPrice,
    pricingPlan,
    isJustRegister,
    isCertificateAndCMEIsFree,
    openGroupModal,
    isCoursePaid,
    containsWorkshop,
    isCertificateAndCMEText,
    locale,
    isFree,
    isCertificateFreeText,
    certificatePrice,
    errorAlreadyRegistredMemebers,
    bookingStatusId,
    noSeatsAvailableError,
    priceData,
    remindMe,
    remindmeLoading,
    errorMessage,
    hideAlreadyBookedMessage,
    ref,
    refTemp,
    expiredGroupDiscount,
    groupDiscountMessage,
    groupDiscountMessageWithHastag,
    shareLink,
  }
}
export default useEventPricing
